import React, { useRef, useCallback } from 'react'

/**
 * 体育专属数字小键盘 (SportScoreKeyboard - H5 原生版)
 * 特点：
 * 1. 纯虚拟键盘，杜绝系统自带键盘遮挡和弹跳抖动
 * 2. 字段自适应：支持负数 +/- (坐位体前屈)，支持小数与整数过滤
 * 3. 快速辅助按键：[0分]、快捷步进器 [-1]、[+1]、[+10] 或 [+0.1]
 * 4. 快速导航：支持上一项/下一项，或上一位/下一位极速遍历
 * 5. 触感震动反馈 (navigator.vibrate)
 */
export default function SportScoreKeyboard({
  visible = true,
  activeField = '',
  fieldName = '成绩',
  unit = '',
  value = '',
  onChangeValue,
  onPrevField,
  onNextField,
  hasNextField = true,
  hasPrevField = false,
  onNextStudent,
  onPrevStudent,
  hasNextStudent = false,
  hasPrevStudent = false,
  studentInfo = null, // { name: '赵云梦', index: 1, total: 35 }
  nextButtonText = '',
  prevButtonText = '',
  onClose,
  showStudentBar = false,
  showZeroButton = true,
  itemProgressText = '',
}) {
  const backspaceTimerRef = useRef(null)

  if (!visible) return null

  // 微触感震动反馈
  const triggerHaptic = (duration = 10) => {
    try {
      if (typeof window !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(duration)
      }
    } catch {
      // ignore
    }
  }

  // 字段特征识别
  const isSitAndReach = activeField === 'sit_and_reach' || fieldName.includes('前屈')
  const isLongRun = ['run_800m', 'run_1000m', 'run_50m_8x', 'run_50m8x'].includes(activeField) ||
    fieldName.includes('800') || fieldName.includes('1000') || fieldName.includes('8×50') || fieldName.includes('50m×8')
  const isIntegerOnly = ['skip_rope', 'sit_up', 'pull_up', 'vital_capacity'].includes(activeField) ||
    fieldName.includes('绳') || fieldName.includes('起坐') || fieldName.includes('引体') || fieldName.includes('肺活量')

  // 数字输入
  const handleNumberPress = (numStr) => {
    triggerHaptic(10)
    let current = value ? String(value) : ''

    // 如果之前是特殊文字状态（免考/缓考/缺考），直接覆盖
    if (isNaN(parseFloat(current)) && current !== '-' && current !== '') {
      current = ''
    }

    // 限制单项最大输入长度
    if (current.replace('-', '').length >= 6) return

    // 中长跑分秒限制
    if (isLongRun && current.includes('.')) {
      const parts = current.split('.')
      if (parts[1] && parts[1].length >= 2) return
    }

    // 整数项目过滤小数点
    if (isIntegerOnly && numStr === '.') return

    if (current === '0' && numStr !== '.') {
      current = numStr
    } else {
      current += numStr
    }

    onChangeValue && onChangeValue(current)
  }

  // 小数点输入
  const handleDotPress = () => {
    triggerHaptic(10)
    if (isIntegerOnly) return
    let current = value ? String(value) : ''

    if (isNaN(parseFloat(current)) && current !== '-' && current !== '') {
      current = ''
    }

    if (current.includes('.')) return

    if (current === '' || current === '-') {
      current += '0.'
    } else {
      current += '.'
    }

    onChangeValue && onChangeValue(current)
  }

  // 正负号反转 (坐位体前屈专属)
  const handleToggleSign = () => {
    triggerHaptic(12)
    let current = value ? String(value) : ''
    if (current === '') {
      current = '-'
    } else if (current === '-') {
      current = ''
    } else if (current.startsWith('-')) {
      current = current.substring(1)
    } else {
      current = '-' + current
    }
    onChangeValue && onChangeValue(current)
  }

  // 单字退格
  const handleBackspace = () => {
    triggerHaptic(12)
    let current = value ? String(value) : ''
    if (!current) return
    const nextVal = current.slice(0, -1)
    onChangeValue && onChangeValue(nextVal)
  }

  // 长按清空
  const handleTouchStartBackspace = () => {
    backspaceTimerRef.current = setTimeout(() => {
      triggerHaptic(25)
      onChangeValue && onChangeValue('')
    }, 600)
  }

  const handleTouchEndBackspace = () => {
    if (backspaceTimerRef.current) {
      clearTimeout(backspaceTimerRef.current)
      backspaceTimerRef.current = null
    }
  }

  // 步进微调
  const handleStep = (delta) => {
    triggerHaptic(12)
    let current = value ? String(value) : ''
    let num = parseFloat(current)
    if (isNaN(num)) {
      num = 0
    }
    let result = num + delta
    if (isIntegerOnly) {
      result = Math.max(0, Math.round(result))
      onChangeValue && onChangeValue(String(result))
    } else {
      const decimals = isLongRun ? 2 : 1
      result = parseFloat(result.toFixed(decimals))
      onChangeValue && onChangeValue(String(result))
    }
  }

  // 快捷业务状态/快速置0
  const handleQuickStatus = (statusText) => {
    triggerHaptic(15)
    onChangeValue && onChangeValue(statusText)
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0f172a] text-white shadow-[0_-8px_30px_rgba(0,0,0,0.35)] select-none border-t border-white/10 pb-safe transition-transform duration-200">
      {/* 1. 顶部辅助条 (Accessory Bar) - 空间留足给项目名称 */}
      <div className="px-3 py-2 bg-[#1e293b]/95 border-b border-white/10 flex items-center justify-between gap-2">
        {/* 左侧：字段与单位 (充分展开，绝对不截断) */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1 mr-1">
          {itemProgressText && (
            <span className="px-1.5 py-0.5 rounded text-[10.5px] font-bold bg-[#056b4f]/20 text-[#34d399] border border-[#056b4f]/40 shrink-0">
              {itemProgressText}
            </span>
          )}
          <span className="text-[13.5px] font-bold text-white whitespace-nowrap tracking-tight">
            {fieldName}
          </span>
          {unit && (
            <span className="text-[11.5px] text-[#94a3b8] shrink-0 font-medium">
              ({unit})
            </span>
          )}
        </div>

        {/* 右侧：步进器、0分与收起按钮 */}
        <div className="flex items-center gap-1 shrink-0">
          {/* 快捷步进 */}
          {isIntegerOnly ? (
            <>
              <button
                type="button"
                onClick={() => handleStep(-1)}
                className="w-8 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                -1
              </button>
              <button
                type="button"
                onClick={() => handleStep(1)}
                className="w-8 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleStep(10)}
                className="w-9 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                +10
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleStep(-1)}
                className="w-8 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                -1
              </button>
              <button
                type="button"
                onClick={() => handleStep(1)}
                className="w-8 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleStep(0.1)}
                className="w-9 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11px] font-bold text-white/90 flex items-center justify-center transition-all"
              >
                +0.1
              </button>
            </>
          )}

          {/* 快速置0 */}
          {showZeroButton && (
            <button
              type="button"
              onClick={() => handleQuickStatus('0')}
              className="px-2.5 h-7 rounded-lg bg-white/10 border border-white/20 text-[#cbd5e1] text-[11.5px] font-bold active:bg-white/25 flex items-center justify-center transition-all"
            >
              0分
            </button>
          )}

          {/* 收起键盘按钮 */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic(10)
              onClose && onClose()
            }}
            className="ml-0.5 pl-1.5 pr-2 h-7 rounded-lg bg-white/10 active:bg-white/25 text-[11.5px] font-medium text-white/80 flex items-center gap-0.5 transition-all"
          >
            <span>收起</span>
            <span className="text-[10px]">▼</span>
          </button>
        </div>
      </div>

      {/* 2. 主按键区 (3列数字 + 1列操作键) */}
      <div className="p-2 grid grid-cols-4 gap-2 max-w-lg mx-auto">
        {/* 数字区 1-9 */}
        <div className="col-span-3 grid grid-cols-3 gap-2">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(num => (
            <button
              key={num}
              type="button"
              onClick={() => handleNumberPress(num)}
              className="h-12 rounded-xl bg-[#1e293b] active:bg-[#334155] active:scale-[0.98] text-[21px] font-bold text-white shadow-sm flex items-center justify-center transition-transform"
            >
              {num}
            </button>
          ))}

          {/* 第 4 行 */}
          {isSitAndReach ? (
            <button
              type="button"
              onClick={handleToggleSign}
              className="h-12 rounded-xl bg-[#334155] active:bg-[#475569] active:scale-[0.98] text-[17px] font-bold text-[#34d399] shadow-sm flex items-center justify-center transition-transform"
            >
              +/-
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleNumberPress('00')}
              className="h-12 rounded-xl bg-[#1e293b] active:bg-[#334155] active:scale-[0.98] text-[16px] font-bold text-white/80 shadow-sm flex items-center justify-center transition-transform"
            >
              00
            </button>
          )}

          <button
            type="button"
            onClick={() => handleNumberPress('0')}
            className="h-12 rounded-xl bg-[#1e293b] active:bg-[#334155] active:scale-[0.98] text-[21px] font-bold text-white shadow-sm flex items-center justify-center transition-transform"
          >
            0
          </button>

          {isIntegerOnly ? (
            <div className="h-12 rounded-xl bg-[#1e293b]/40 text-[#475569] flex items-center justify-center text-[18px]">
              ·
            </div>
          ) : (
            <button
              type="button"
              onClick={handleDotPress}
              className="h-12 rounded-xl bg-[#1e293b] active:bg-[#334155] active:scale-[0.98] text-[22px] font-bold text-white shadow-sm flex items-center justify-center transition-transform"
            >
              .
            </button>
          )}
        </div>

        {/* 动作列 (退格、上一项/位、下一项/位) */}
        <div className="flex flex-col gap-2">
          {/* 退格按键 */}
          <button
            type="button"
            onClick={handleBackspace}
            onTouchStart={handleTouchStartBackspace}
            onTouchEnd={handleTouchEndBackspace}
            onMouseDown={handleTouchStartBackspace}
            onMouseUp={handleTouchEndBackspace}
            className="h-12 rounded-xl bg-[#334155] active:bg-[#475569] active:scale-[0.98] text-[#f87171] shadow-sm flex items-center justify-center transition-transform"
            title="退格 (长按清空)"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
              <line x1="18" y1="9" x2="12" y2="15"></line>
              <line x1="12" y1="9" x2="18" y2="15"></line>
            </svg>
          </button>

          {/* 上一项 / 上一位 */}
          <button
            type="button"
            disabled={!hasPrevField && !hasPrevStudent}
            onClick={() => {
              triggerHaptic(10)
              if (onPrevField) onPrevField()
              else if (onPrevStudent) onPrevStudent()
            }}
            className={`h-12 rounded-xl text-[13px] font-bold shadow-sm flex items-center justify-center transition-transform ${
              !hasPrevField && !hasPrevStudent
                ? 'bg-[#1e293b]/40 text-[#475569] cursor-not-allowed'
                : 'bg-[#334155] active:bg-[#475569] text-white/90 active:scale-[0.98]'
            }`}
          >
            {prevButtonText || (onPrevStudent ? '上一位' : '上一项')}
          </button>

          {/* 下一项 / 下一位 (高亮主按键) */}
          <button
            type="button"
            onClick={() => {
              triggerHaptic(15)
              if (onNextField) onNextField()
              else if (onNextStudent) onNextStudent()
              else if (onClose) onClose()
            }}
            className="flex-1 min-h-[104px] rounded-xl bg-gradient-to-b from-[#056b4f] to-[#04523c] active:from-[#04523c] active:to-[#04523c] active:scale-[0.98] text-[15px] font-extrabold text-white shadow-md flex flex-col items-center justify-center gap-1 transition-transform"
          >
            <span>{nextButtonText || (onNextStudent ? '下一位' : '下一项')}</span>
            <span className="text-[11px] font-normal text-white/80">
              {onNextStudent ? '自动保存' : '完成输入'}
            </span>
          </button>
        </div>
      </div>

      {/* 3. 可选底栏：学生导航切换条 */}
      {showStudentBar && studentInfo && (
        <div className="px-4 py-2 bg-[#1e293b]/90 border-t border-white/10 flex items-center justify-between text-[12px] max-w-lg mx-auto">
          <button
            type="button"
            disabled={!hasPrevStudent}
            onClick={() => {
              triggerHaptic(12)
              onPrevStudent && onPrevStudent()
            }}
            className={`flex items-center gap-1 font-bold ${
              hasPrevStudent ? 'text-[#34d399] active:opacity-70' : 'text-[#64748b] cursor-not-allowed'
            }`}
          >
            <span>◀ 上一位</span>
          </button>

          <div className="flex items-center gap-1.5 font-bold text-white">
            <span>{studentInfo.name}</span>
            <span className="text-[11px] text-[#94a3b8]">
              (第 {studentInfo.index + 1} / {studentInfo.total} 人)
            </span>
          </div>

          <button
            type="button"
            disabled={!hasNextStudent}
            onClick={() => {
              triggerHaptic(12)
              onNextStudent && onNextStudent()
            }}
            className={`flex items-center gap-1 font-bold ${
              hasNextStudent ? 'text-[#34d399] active:opacity-70' : 'text-[#64748b] cursor-not-allowed'
            }`}
          >
            <span>下一位 ▶</span>
          </button>
        </div>
      )}
    </div>
  )
}

